import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { CONSENT_CHANNELS, SALES_GOAL_LABEL, SALES_GOALS, OFFER_KIND_LABEL, formatGhs } from '@/client/types/sales';
import {
  usePatchPolicyMutation,
  useSalesMeQuery,
  useSalesOffersQuery,
  useSalesPolicyQuery,
  useSalesPurposesQuery,
  useSalesSelfCheckQuery,
  useSalesSweepMutation,
  useUpsertOfferMutation,
  useUpsertPurposeMutation,
} from '@/client/api/sales/sales';
import { SalesPageShell } from './components/SalesPageShell';
import { Panel, Segmented, StatusBadge, btnGhost, btnPrimary, selectCls, toastSales } from './components/sales-ui';

type PolicyTab = 'purposes' | 'offers' | 'rules';

export default function SalesPolicyPage() {
  const [tab, setTab] = useState<PolicyTab>('purposes');
  const me = useSalesMeQuery();
  const purposesQ = useSalesPurposesQuery();
  const offersQ = useSalesOffersQuery();
  const policyQ = useSalesPolicyQuery();
  const checkQ = useSalesSelfCheckQuery();
  const upsertPurpose = useUpsertPurposeMutation();
  const upsertOffer = useUpsertOfferMutation();
  const patchPolicy = usePatchPolicyMutation();
  const sweep = useSalesSweepMutation();
  const purposes = purposesQ.data ?? [];
  const offers = offersQ.data ?? [];
  const policy = policyQ.data;
  const manage = me.data?.canManageRules === true;
  const [weights, setWeights] = useState(policy?.fitWeights);

  useEffect(() => {
    if (policy?.fitWeights) setWeights(policy.fitWeights);
  }, [policy?.fitWeights]);

  const save = (patch: Parameters<typeof patchPolicy.mutate>[0]) => {
    patchPolicy.mutate(patch, {
      onSuccess: () => toast.success('Saved'),
      onError: (err) => toastSales(err),
    });
  };

  return (
    <SalesPageShell
      title="Rules"
      description="Purposes set GHS on a claim. Offers are catalog items. Protections are lock, cooldown, outreach spacing, and fit."
      crumbs={[{ label: 'Rules' }]}
      icon={SlidersHorizontal}
      actions={
        <Segmented
          value={tab}
          onChange={setTab}
          options={[
            { id: 'purposes', label: 'Purposes' },
            { id: 'offers', label: 'Offers' },
            { id: 'rules', label: 'Protections' },
          ]}
        />
      }
    >
      {!manage && (
        <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
          You can read rules. Editing needs sales.rules.manage — no need to act as anyone.
        </p>
      )}
      {tab === 'purposes' && (
        <div className="space-y-3">
          {manage && (
            <button
              className={btnPrimary}
              disabled={upsertPurpose.isPending}
              onClick={() =>
                upsertPurpose.mutate(
                  {
                    name: 'New purpose',
                    goal: 'signup',
                    estimatedValueGhs: 400,
                    active: true,
                    targetOutcome: 'signup',
                    defaultClaimDays: 14,
                  },
                  {
                    onSuccess: () => toast.success('Purpose created'),
                    onError: (err) => toastSales(err),
                  },
                )
              }
            >
              New purpose
            </button>
          )}
          {purposes.length === 0 && (
            <p className="text-sm text-gray-500">No purposes yet. Claims fail with missing_purpose until you add one per goal.</p>
          )}
          {purposes.map((p) => (
            <div key={p.id} className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-center gap-2">
                <input
                  className="rounded border-gray-200 text-sm font-semibold"
                  defaultValue={p.name}
                  disabled={!manage}
                  onBlur={(e) =>
                    manage &&
                    upsertPurpose.mutate(
                      { id: p.id, name: e.target.value },
                      { onError: (err) => toastSales(err) },
                    )
                  }
                />
                <StatusBadge label={SALES_GOAL_LABEL[p.goal]} className="border bg-gray-50 text-gray-700" />
                <span className="text-sm text-gray-500">{formatGhs(p.estimatedValueGhs)}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-2 text-sm">
                <select
                  className={selectCls}
                  value={p.goal}
                  disabled={!manage}
                  onChange={(e) =>
                    manage &&
                    upsertPurpose.mutate(
                      { id: p.id, goal: e.target.value as typeof p.goal },
                      { onError: (err) => toastSales(err) },
                    )
                  }
                >
                  {SALES_GOALS.map((g) => (
                    <option key={g} value={g}>
                      {SALES_GOAL_LABEL[g]}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  className={selectCls}
                  defaultValue={p.estimatedValueGhs}
                  disabled={!manage}
                  onBlur={(e) =>
                    manage &&
                    upsertPurpose.mutate(
                      { id: p.id, estimatedValueGhs: Number(e.target.value) || 0 },
                      { onError: (err) => toastSales(err) },
                    )
                  }
                />
                <label className="flex items-center gap-1 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    checked={p.active}
                    disabled={!manage}
                    onChange={(e) =>
                      manage &&
                      upsertPurpose.mutate(
                        { id: p.id, active: e.target.checked },
                        { onError: (err) => toastSales(err) },
                      )
                    }
                  />
                  Active
                </label>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'offers' && (
        <div className="space-y-3">
          {manage && (
            <button
              className={btnPrimary}
              disabled={upsertOffer.isPending}
              onClick={() =>
                upsertOffer.mutate(
                  {
                    name: 'New offer',
                    kind: 'voucher',
                    costGhs: 50,
                    capPerClaim: 1,
                    active: true,
                    modulePath: '/admin/vouchers',
                  },
                  {
                    onSuccess: () => toast.success('Offer created'),
                    onError: (err) => toastSales(err),
                  },
                )
              }
            >
              New offer
            </button>
          )}
          {offers.length === 0 && <p className="text-sm text-gray-500">No offers yet.</p>}
          {offers.map((o) => (
            <div key={o.id} className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-semibold text-gray-900">{o.name}</div>
                  <div className="text-xs text-gray-500">
                    {OFFER_KIND_LABEL[o.kind]} · {formatGhs(o.costGhs)} · cap {o.capPerClaim}
                  </div>
                </div>
                {o.modulePath ? (
                  <Link to={o.modulePath} className="text-sm text-blue-700 hover:underline">
                    Open module
                  </Link>
                ) : null}
              </div>
              <label className="mt-2 flex items-center gap-1 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={o.active}
                  disabled={!manage}
                  onChange={(e) =>
                    manage &&
                    upsertOffer.mutate(
                      { id: o.id, active: e.target.checked },
                      { onError: (err) => toastSales(err) },
                    )
                  }
                />
                Active
              </label>
            </div>
          ))}
        </div>
      )}

      {tab === 'rules' && policy && (
        <div className="space-y-4">
          <Panel title="Cooldowns and capacity">
            <div className="grid gap-3 sm:grid-cols-3 text-sm">
              {(
                [
                  ['releaseDays', 'Release days'],
                  ['expiredDays', 'Expired days'],
                  ['lostDays', 'Lost days'],
                  ['wonSameGoalDays', 'Won same-goal days'],
                  ['nextGoalHandoffDays', 'Next-goal handoff days'],
                  ['maxConcurrentPerRep', 'Max concurrent'],
                  ['firstTouchHours', 'First-touch hours'],
                  ['idleDays', 'Idle days'],
                  ['researchResurfaceDays', 'Marketplace skip days'],
                  ['trackedLinkTtlHours', 'Tracked-link TTL hours'],
                ] as const
              ).map(([key, label]) => (
                <label key={key} className="text-xs text-gray-500">
                  {label}
                  <input
                    type="number"
                    className={`${selectCls} mt-1 w-full`}
                    defaultValue={policy[key]}
                    disabled={!manage}
                    onBlur={(e) => manage && save({ [key]: Number(e.target.value) || 0 })}
                  />
                </label>
              ))}
            </div>
          </Panel>

          <Panel title="Outreach spacing (hours)">
            <div className="grid gap-3 sm:grid-cols-3 text-sm">
              {(['whatsapp', 'sms', 'phone', 'email', 'in_app'] as const).map((ch) => (
                <label key={ch} className="text-xs text-gray-500">
                  {ch}
                  <input
                    type="number"
                    className={`${selectCls} mt-1 w-full`}
                    defaultValue={policy.outreachMinHours[ch] ?? 0}
                    disabled={!manage}
                    onBlur={(e) =>
                      manage &&
                      save({
                        outreachMinHours: {
                          ...policy.outreachMinHours,
                          [ch]: Number(e.target.value) || 0,
                        },
                      })
                    }
                  />
                </label>
              ))}
            </div>
          </Panel>

          <Panel title="Channel caps">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
              {CONSENT_CHANNELS.map((ch) => {
                const cap = policy.channelCaps[ch];
                return (
                  <div key={ch} className="rounded-lg border border-gray-100 p-3">
                    <p className="mb-2 text-xs font-medium text-gray-700">{ch}</p>
                    <label className="block text-xs text-gray-500">
                      Max
                      <input
                        type="number"
                        className={`${selectCls} mt-1 w-full`}
                        defaultValue={cap?.max ?? 0}
                        disabled={!manage}
                        onBlur={(e) => {
                          if (!manage) return;
                          const max = Number(e.target.value) || 0;
                          const days = policy.channelCaps[ch]?.days ?? 14;
                          const next = { ...policy.channelCaps };
                          if (max <= 0) delete next[ch];
                          else next[ch] = { max, days };
                          save({ channelCaps: next });
                        }}
                      />
                    </label>
                    <label className="mt-2 block text-xs text-gray-500">
                      Days
                      <input
                        type="number"
                        className={`${selectCls} mt-1 w-full`}
                        defaultValue={cap?.days ?? 14}
                        disabled={!manage}
                        onBlur={(e) => {
                          if (!manage) return;
                          const days = Number(e.target.value) || 0;
                          const max = policy.channelCaps[ch]?.max ?? 0;
                          if (max <= 0) return;
                          save({ channelCaps: { ...policy.channelCaps, [ch]: { max, days } } });
                        }}
                      />
                    </label>
                  </div>
                );
              })}
            </div>
          </Panel>

          <Panel title="Fit weights (normalized on save)">
            <div className="grid gap-3 sm:grid-cols-4 text-sm">
              {(['areaDemand', 'activityRecency', 'spendBand', 'goalGap'] as const).map((k) => (
                <label key={k} className="text-xs text-gray-500">
                  {k}
                  <input
                    type="number"
                    step="0.05"
                    className={`${selectCls} mt-1 w-full`}
                    value={weights?.[k] ?? 0}
                    disabled={!manage}
                    onChange={(e) =>
                      manage && setWeights((w) => (w ? { ...w, [k]: Number(e.target.value) } : w))
                    }
                  />
                </label>
              ))}
            </div>
            {manage && weights && (
              <button className={`${btnGhost} mt-3`} onClick={() => save({ fitWeights: weights })}>
                Save weights
              </button>
            )}
          </Panel>

          <Panel title="Self-check">
            <ul className="space-y-1 text-sm">
              {(checkQ.data?.rows ?? []).map((c) => (
                <li
                  key={`${c.code}-${c.message}`}
                  className={c.severity === 'error' ? 'text-rose-700' : 'text-amber-800'}
                >
                  {c.message} {c.fix}
                </li>
              ))}
              {checkQ.data?.ok && <li className="text-emerald-700">All checks passed.</li>}
            </ul>
            {manage && (
              <button
                className={`${btnGhost} mt-3`}
                disabled={sweep.isPending}
                onClick={() =>
                  sweep.mutate(undefined as never, {
                    onSuccess: () => toast.success('Sweep queued'),
                    onError: (err) => toastSales(err),
                  })
                }
              >
                Run expiry sweep
              </button>
            )}
          </Panel>
        </div>
      )}
    </SalesPageShell>
  );
}
